import { Box, FormControl, MenuItem, Pagination, Paper, Select, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import RadiusButton from '../../../../components/RadiusButton';
import { getVenueSeatMappingUrl, SampleValue } from './dashboardPlaceholders';
import ACTION_VIEW from '../../../../assets/images/action-view.svg';

const PAGE_SIZES = [10, 25, 50];

const tabs = ['Download', 'Exam', 'Biometric', 'Upload', 'Alert'];
const ALERT_TAB_INDEX = tabs.indexOf('Alert');

const firstColumns = [
    { id: 'srNo', label: 'Sr No', minWidth: 60 },
    { id: 'city', label: 'City', minWidth: 100, sample: true },
    { id: 'hallName', label: 'Center', minWidth: 140 },
];

// Columns exactly as in the approved design (CET_PGP_Dashboard.html).
// sample: true = no data on central yet, value comes from dashboardPlaceholders
const columnsByTab = {
    Download: [
        ...firstColumns,
        { id: 'examExpected', label: 'Exam Expected', minWidth: 110 },
        { id: 'examDownloaded', label: 'Exam Downloaded', minWidth: 110 },
        { id: 'candidatesDataDownloaded', label: 'Exam Status', minWidth: 110, status: true },
        { id: 'qpExpected', label: 'QP Expected', minWidth: 90 },
        { id: 'qpDownloaded', label: 'QP Downloaded', minWidth: 90 },
        { id: 'qPacksDownloaded', label: 'QP Status', minWidth: 110, status: true },
    ],
    Exam: [
        { id: 'view', label: '', minWidth: 50, noSort: true },
        ...firstColumns,
        { id: 'scheduled', label: 'Sched', minWidth: 70 },
        { id: 'present', label: 'Pres', minWidth: 70 },
        { id: 'loggedIn', label: 'Login', minWidth: 70 },
        { id: 'started', label: 'Started', minWidth: 70 },
        { id: 'completed', label: 'Compl', minWidth: 70 },
        { id: 'firstLogin', label: 'First Login', minWidth: 150 },
        { id: 'lastLogin', label: 'Last Login', minWidth: 150 },
        { id: 'syncOn', label: 'Sync', minWidth: 70, sample: true },
    ],
    Biometric: [
        ...firstColumns,
        { id: 'scheduled', label: 'Total Scheduled', minWidth: 110 },
        { id: 'biometricCaptured', label: 'Biometric Done', minWidth: 110 },
        { id: 'biometricPending', label: 'Biometric Pending', minWidth: 110 },
    ],
    Upload: [
        ...firstColumns,
        { id: 'present', label: 'Rpack Expected', minWidth: 110 },
        { id: 'rPacksUploaded', label: 'Rpack Uploaded', minWidth: 110 },
        { id: 'rpackStatus', label: 'Rpack Status', minWidth: 110, status: true },
        { id: 'biometricExpected', label: 'Biometric Expected', minWidth: 120 },
        { id: 'biometricUploaded', label: 'Biometric Uploaded', minWidth: 120, sample: true },
        { id: 'biometricUploadStatus', label: 'Biometric Status', minWidth: 120, sample: true, status: true },
    ],
    // Alert tab, as in the design: one row per venue with its open alert count (real deviceLog alerts; code / city / state are sample)
    Alert: [
        { id: 'srNo', label: 'Sr No', minWidth: 60 },
        { id: 'venueCode', label: 'Venue Code', minWidth: 100, sample: true },
        { id: 'venueName', label: 'Venue Name', minWidth: 140 },
        { id: 'city', label: 'City', minWidth: 100, sample: true },
        { id: 'state', label: 'State', minWidth: 100, sample: true },
        { id: 'alertCount', label: 'Alert Count', minWidth: 90 },
        { id: 'alertView', label: 'View', minWidth: 50, noSort: true },
    ],
};

// Candidate counts that open the center popup filtered to that status (list-for-server-summary "status"),
// per tab. Download tab numbers are per server (1/0), not candidates, so they are not clickable.
const statusFilters = {
    Exam: {
        scheduled: { status: undefined, statusLabel: 'Scheduled' },
        present: { status: 'present', statusLabel: 'Present' },
        loggedIn: { status: 'logged_in', statusLabel: 'Login' },
        started: { status: 'exam_started', statusLabel: 'Started' },
        completed: { status: 'exam_submitted', statusLabel: 'Completed' },
    },
    Biometric: {
        scheduled: { status: undefined, statusLabel: 'Total Scheduled' },
        biometricCaptured: { status: 'biometric_done', statusLabel: 'Biometric Done' },
        biometricPending: { status: 'biometric_pending', statusLabel: 'Biometric Pending' },
    },
    Upload: {
        present: { status: 'present', statusLabel: 'Rpack Expected' },
        rPacksUploaded: { status: 'rpack_uploaded', statusLabel: 'Rpack Uploaded' },
        biometricExpected: { status: 'present', statusLabel: 'Biometric Expected' },
    },
};

// Row needs attention: a step is incomplete or biometric is pending (same rules as the design).
const needsAttention = (tab, center) => {
    switch (tab) {
        case 'Download':
            return !center.candidatesDataDownloaded || !center.qPacksDownloaded;
        case 'Biometric':
            return center.biometricPending > 0;
        case 'Upload':
            return !center.rpackStatus || !center.biometricUploadStatus;
        case 'Alert':
            return center.alertCount > 0;
        default:
            return false;
    }
}

// Plain text value of a cell, used for search, sort and CSV
const getPlainValue = (columnId, center) => {
    const value = center[columnId];
    switch (columnId) {
        case 'firstLogin':
        case 'lastLogin':
            return value ? value.format('DD-MM-YYYY HH:mm:ss') : '-';
        case 'syncOn':
            return value ? 'Running' : 'Stopped';
        default:
            if (typeof value === 'boolean') {
                return value ? 'Complete' : 'Incomplete';
            }
            return value ?? '-';
    }
}

// PC with heartbeat line; animated while sync is running
const SyncIcon = ({ on }) => (
    <Tooltip title={on ? 'Sync running' : 'Sync stopped'} arrow>
        <Box component='svg' viewBox="0 0 30 26" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-label={on ? 'Sync running' : 'Sync stopped'} sx={{
            width: 30,
            height: 26,
            verticalAlign: 'middle',
            '& .pc': { stroke: (theme) => theme.palette.grey[600] },
            '& .beat': {
                stroke: (theme) => on ? theme.palette.success.main : theme.palette.error.main,
                ...(on ? { strokeDasharray: '38 62', animation: 'dashboardSyncBeat 1.4s linear infinite' } : {}),
            },
            '@keyframes dashboardSyncBeat': { from: { strokeDashoffset: 100 }, to: { strokeDashoffset: 0 } },
            '@media (prefers-reduced-motion: reduce)': { '& .beat': { animationDuration: '4s' } },
        }}>
            <rect className="pc" x="2" y="2" width="26" height="17" rx="2" strokeWidth="1.8" />
            <path className="pc" d="M11 24h8M15 19v5" strokeWidth="1.8" />
            <polyline className="beat" pathLength="100" strokeWidth="1.8" points={on ? '5,11 10,11 12,6 14,15 16,8 18,11 25,11' : '5,11 25,11'} />
        </Box>
    </Tooltip>
);

// A step is done for a center when every present candidate has it (and someone is present)
const isDoneForAllPresent = (doneCount, presentCount) => presentCount > 0 && doneCount >= presentCount;

const toCenterRow = (center, index) => ({
    ...center,
    srNo: index + 1,
    absent: center.scheduled - center.present,
    biometricPending: Math.max(0, center.scheduled - center.biometricCaptured),
    examExpected: 1,
    examDownloaded: center.examDataDownloaded ? 1 : 0,
    qpExpected: 1,
    qpDownloaded: center.qPacksDownloaded ? 1 : 0,
    rpackStatus: isDoneForAllPresent(center.rPacksUploaded, center.present),
    biometricExpected: center.present,
    biometricUploadStatus: isDoneForAllPresent(center.biometricUploaded, center.present),
});

const toVenueRow = (venue, index) => ({
    ...venue,
    key: `venue-${venue.venueMasterId}`,
    srNo: index + 1,
    venueName: venue.venueName ?? `Venue ${venue.venueMasterId}`,
    alertCount: venue.openCount,
});

// Sort value of a cell: numbers as numbers (valueOf turns booleans and dayjs dates into numbers), otherwise text
const getSortValue = (row, columnId) => row[columnId]?.valueOf?.() ?? row[columnId] ?? getPlainValue(columnId, row);

const compareRows = (rowA, rowB, columnId) => {
    const valueA = getSortValue(rowA, columnId);
    const valueB = getSortValue(rowB, columnId);
    if (typeof valueA === 'number' && typeof valueB === 'number') {
        return valueA - valueB;
    }
    return String(valueA ?? '').localeCompare(String(valueB ?? ''));
}

const toCsvLine = (values) => values.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',');

export const CenterReport = ({ centers, onCenterClick, alertVenues = [], onAlertClick, alertTabRequest }) => {

    const params = useParams();
    const projectName = params?.projectName;

    const [selectedTab, setSelectedTab] = useState(1);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
    const [sort, setSort] = useState({ id: 'srNo', direction: 'asc' });
    const [copyLabel, setCopyLabel] = useState('Copy CSV');

    const tab = tabs[selectedTab];
    const columns = columnsByTab[tab];

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedTab, search, centers, alertVenues, pageSize]);

    // "Open Alert tab" from the header bell
    useEffect(() => {
        if (alertTabRequest) {
            setSelectedTab(ALERT_TAB_INDEX);
        }
    }, [alertTabRequest]);

    const openAlertCount = alertVenues.reduce((sum, venue) => sum + venue.openCount, 0);

    // Alerts belong to venues (several exams can share one), so the Alert tab lists venues instead of centers
    const allRows = tab === 'Alert' ? alertVenues.map(toVenueRow) : centers.map(toCenterRow);

    // Icon columns (no sort) are left out of search and CSV
    const dataColumns = columns.filter((column) => !column.noSort);
    const searchTerm = search.toLowerCase();
    const sortDirection = sort.direction === 'asc' ? 1 : -1;

    const rows = allRows
        .filter((row) => !searchTerm || dataColumns.some((column) => String(getPlainValue(column.id, row)).toLowerCase().includes(searchTerm)))
        .sort((rowA, rowB) => compareRows(rowA, rowB, sort.id) * sortDirection);

    const maxPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const pageRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const onSort = (id) => {
        setSort((prev) => ({ id, direction: prev.id === id && prev.direction === 'asc' ? 'desc' : 'asc' }));
    }

    const onCopyCsv = async () => {
        const csv = [
            toCsvLine(dataColumns.map((column) => column.label)),
            ...rows.map((row) => toCsvLine(dataColumns.map((column) => getPlainValue(column.id, row)))),
        ].join('\n');
        try {
            await navigator.clipboard.writeText(csv);
            setCopyLabel('Copied');
        } catch (error) {
            console.log('There is an error while copying CSV', error);
            setCopyLabel('Copy failed');
        }
        setTimeout(() => setCopyLabel('Copy CSV'), 1500);
    }

    const renderCellValue = (column, center) => {
        const value = center[column.id];
        if (column.id === 'syncOn') {
            return <SyncIcon on={!!value} />;
        }
        if (column.status) {
            return <Box component='span' sx={{
                cursor: 'default',
                color: value ? 'success.main' : 'error.main',
            }}>
                {value ? 'Complete' : 'Incomplete'}
            </Box>
        }
        switch (column.id) {
            case 'alertView':
                return <Box component='span' sx={{ cursor: 'pointer' }} aria-label={`View alerts for ${center.venueName}`} onClick={() => onAlertClick(center)}>
                    <img src={ACTION_VIEW} alt='View alerts' />
                </Box>
            case 'alertCount':
                return <Box component='span' sx={{ color: value > 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>{value}</Box>
            case 'view':
                return <Tooltip title='Seat Mapping' arrow>
                    <Box component='span' sx={{ cursor: 'pointer' }} onClick={() => window.open(getVenueSeatMappingUrl(projectName), '_blank', 'noopener,noreferrer')}>
                        <img src={ACTION_VIEW} alt='View seat mapping' />
                    </Box>
                </Tooltip>
            case 'hallName':
                return <Box component='span' sx={{
                    cursor: 'pointer',
                    color: 'primary.main',
                    textDecoration: 'underline',
                }} onClick={() => onCenterClick(center)}>
                    {center.hallName}
                </Box>
            case 'biometricPending':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: value > 0 ? 'error.main' : 'success.main',
                }}>
                    {value}
                </Box>
            default:
                return getPlainValue(column.id, center);
        }
    }

    const renderTableCell = (column, center) => {
        const filter = statusFilters[tab]?.[column.id];
        if (filter && !column.sample && center[column.id] > 0) {
            return <Box component='span' sx={{
                cursor: 'pointer',
                color: column.id === 'biometricPending' ? 'error.main' : 'primary.main',
                textDecoration: 'underline',
            }} onClick={() => onCenterClick({ ...center, ...filter })}>
                {center[column.id]}
            </Box>
        }
        const content = renderCellValue(column, center);
        return column.sample ? <SampleValue>{content}</SampleValue> : content;
    }

    return (
        <Box sx={{
            width: '100%',
            bgcolor: 'background.base',
            p: 1.5,
            borderRadius: 2,
        }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}>Detailed Report</Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} aria-label="detailed report tabs">
                    {tabs.map((t, index) => <Tab key={t} sx={{ fontSize: 14, fontWeight: 500 }} id={`report-tab-${index}`} aria-controls={`report-tabpanel-${index}`}
                        label={t === 'Alert' && openAlertCount
                            ? <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                {t}
                                <Box component='span' aria-label={`${openAlertCount} open`} sx={{ minWidth: 18, height: 18, px: 0.5, borderRadius: 2, bgcolor: 'error.main', color: '#FFFFFF', fontSize: 11, fontWeight: 700, display: 'grid', placeItems: 'center' }}>
                                    {openAlertCount}
                                </Box>
                            </Box>
                            : t} />)}
                </Tabs>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', my: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, color: 'grey.600' }}>Show</Typography>
                    <FormControl size='small'>
                        <Select value={pageSize} onChange={(e) => setPageSize(e.target.value)} sx={{ fontSize: 13 }}>
                            {PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: 13, color: 'grey.600' }}>entries</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <div className='search-wrapper'>
                        <input type='text' value={search} onChange={(e) => setSearch(e.target.value)} className='search-box' placeholder='Search...' />
                    </div>
                    <RadiusButton sx={{ fontWeight: 500, textTransform: 'uppercase' }} color="secondary" variant="outlined" onClick={onCopyCsv}>
                        {copyLabel}
                    </RadiusButton>
                </Box>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none', mb: 1.5 }}>
                <TableContainer sx={{
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1.5
                }}>
                    <Table stickyHeader aria-label="detailed report table" id={`report-tabpanel-${selectedTab}`}>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        sx={{ bgcolor: 'grey.100', color: 'grey.600', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap', px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'grey.300', cursor: column.noSort ? 'default' : 'pointer', userSelect: 'none' }}
                                        key={column.id}
                                        onClick={column.noSort ? undefined : () => onSort(column.id)}
                                    >
                                        {column.label}
                                        {!column.noSort && (
                                            <Box component='span' sx={{ opacity: 0.45, ml: 0.5 }}>
                                                {sort.id === column.id ? (sort.direction === 'asc' ? '▲' : '▼') : '↕'}
                                            </Box>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!pageRows.length
                                ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                    <TableCell colSpan={columns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                        No records available
                                    </TableCell>
                                </TableRow>
                                : pageRows.map((center) => {
                                    return <TableRow hover={false} key={center.key}>
                                        {columns.map((column, columnIndex) => {
                                            return <TableCell key={column.id} sx={{
                                                px: 1.5,
                                                py: 1.125,
                                                fontSize: 13,
                                                whiteSpace: 'nowrap',
                                                borderColor: 'grey.300',
                                                ...(columnIndex === 0 && needsAttention(tab, center) ? { boxShadow: (theme) => `inset 3px 0 ${theme.palette.error.main}` } : {}),
                                            }}>
                                                {renderTableCell(column, center)}
                                            </TableCell>
                                        })}
                                    </TableRow>
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, color: 'grey.600' }}>
                    {`Showing ${rows.length ? (currentPage - 1) * pageSize + 1 : 0} to ${Math.min(currentPage * pageSize, rows.length)} of ${rows.length} entries`}
                </Typography>
                <Pagination count={maxPages} page={currentPage} onChange={(e, value) => setCurrentPage(value)} shape="rounded" />
            </Box>
            <Typography sx={{ fontSize: 12, color: 'grey.600', mt: 1 }}>
                {tab === 'Alert' ? 'Alert count shows open alerts (not yet resolved).' : 'Select a center name to see its candidate details.'}
            </Typography>
        </Box>
    );
}
